import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import React, { CSSProperties, ReactNode, useCallback, useEffect, useReducer, useRef } from "react";
import { WrapperComponentWithDiv } from "../../arrangement/WrapperComponent";
import cn from "classnames";

import { css, SerializedStyles } from "@emotion/react";

import { CSS_MQ } from "../../../../styles/breakpoints";
import { ProgressBar } from "./subcomponents/ProgressBar";
import styled from "@emotion/styled";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";

import isEqual from "react-fast-compare";
import { FONTS } from "../../../../styles/fonts";
import { useEqualState } from "../../../../hooks";
import { useScrollbarProgress } from "./use-scrollbar-progress";
import { clampProgress } from "./clamp-progress";

export const SwarovskiGenericCarouselComponent: ReactTFC<{
  headline: ReactNode;
  bottomText: string | undefined;
  width: "full" | "content";
  scrollSnap: boolean;
  carouselElements: Array<React.ReactElement>;
  carouselWrapperStyles?: SerializedStyles;
  wrapperContainerStyles?: SerializedStyles;
  imageWidth: number;
  initialProgress?: number | undefined;
  elementsAlignment?: "to-left" | "centered";
  className?: string;
  initiallyCentered?: boolean;
  placeholderForNonFilledState?: boolean;
  background?: "transparent" | "grey";
}> = props => {
  const attachStyles = [
    internalWrapperContainerStyles,
    ...(props.wrapperContainerStyles ? [props.wrapperContainerStyles] : []),
  ];

  const className = cn(
    !props.elementsAlignment || props.elementsAlignment === "centered" ? "centered" : undefined,
    props.className,
  );

  return (
    <WrapperComponentWithDiv
      width={props.width}
      attachStyles={attachStyles}
      className={className}
      style={
        {
          "--w": `${props.imageWidth}px`,
        } as CSSProperties
      }
    >
      <SwarovskiInternalCarousel
        headline={props.headline}
        scrollSnap={props.scrollSnap}
        imageWidth={props.imageWidth}
        initialProgress={props.initialProgress}
        bottomText={props.bottomText}
        carouselElements={props.carouselElements}
        carouselWrapperStyles={props.carouselWrapperStyles}
        initiallyCentered={props.initiallyCentered}
        placeholderForNonFilledState={props.placeholderForNonFilledState}
        background={props.background}
      />
    </WrapperComponentWithDiv>
  );
};

const SwarovskiInternalCarousel: ReactTFC<{
  headline: ReactNode;
  scrollSnap: boolean;
  imageWidth: number;
  initialProgress?: number | undefined;
  bottomText: string | undefined;
  carouselElements: Array<React.ReactElement>;
  carouselWrapperStyles?: SerializedStyles;
  initiallyCentered?: boolean;
  placeholderForNonFilledState?: boolean;
  background?: "transparent" | "grey";
}> = props => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pages, setPages] = useEqualState<{ totalPagesCount: number } | null>(null);
  const [scrollProgress, updateScrollProgress] = useScrollbarProgress();
  const [visiblePerPage, setVisiblePerPage] = useEqualState<number | null>(null);

  const centerContentIfCarouselNotFilledCssClassName: "not-filled" | undefined = pages
    ? pages.totalPagesCount === 1
      ? "not-filled"
      : undefined
    : props.initiallyCentered
      ? "not-filled"
      : undefined;
  const scrollSnapCssClassName = props.scrollSnap ? "scroll-snap" : undefined;

  const updatePage = useCallback((element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }

    const { scrollWidth, clientWidth } = element;

    const pagesCount = Math.ceil(scrollWidth / clientWidth);

    setPages({
      totalPagesCount: pagesCount,
    });
  }, []);

  const updateVisiblePerPage = useCallback((element: HTMLDivElement | null) => {
    if (!element) {
      return;
    }

    const { clientWidth } = element;

    setVisiblePerPage(clientWidth / props.imageWidth);
  }, []);

  const onSeekRequested = useCallback((progress: { seek: number }) => {
    window.requestAnimationFrame(() => {
      const el = ref.current;

      if (el) {
        el.scrollLeft = (el.scrollWidth - el.clientWidth) * clampProgress(progress.seek);
      }
    });
  }, []);

  useEffect(() => {
    const { current } = ref;

    if (!current) {
      console.log("No element found.");
      return;
    }

    updatePage(current);
    updateScrollProgress(current);
    updateVisiblePerPage(current);

    const onScroll = (e: any) => {
      e.preventDefault();

      updatePage(e.target);
      updateScrollProgress(e.target);
      // updateVisiblePerPage(e.target);
    };

    const onResize = () => {
      if (ref.current) {
        updatePage(ref.current);
        updateScrollProgress(ref.current);
        updateVisiblePerPage(ref.current);
      }
    };

    current.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      current.removeEventListener("scroll", onScroll);
      current.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    // This is an unideal but quick implementation of "currently selected element is visible"
    // piece of functionality. It is useful for Product Tech Spec carousel use case
    // where users can navigate between different carousel elements themselves
    // or have a link with already selected element - therefore it's important
    // to always show this selected element initially.

    // TODO: Ideally, it should check if the currently selected index is in view.
    if (typeof props.initialProgress === "number" && ref.current) {
      onSeekRequested({
        seek: props.initialProgress,
      });
    }
  }, [props.initialProgress]);

  return (
    <>
      {props.headline}

      <SwarovskiCarouselStaticPart
        ref={ref}
        className={cn(centerContentIfCarouselNotFilledCssClassName, scrollSnapCssClassName)}
        bottomText={props.bottomText}
        carouselElements={props.carouselElements}
        carouselWrapperStyles={props.carouselWrapperStyles}
        background={props.background}
      />

      <ProgressBar
        scrollProgress={scrollProgress}
        imagesCount={props.carouselElements.length}
        visiblePerPage={visiblePerPage}
        onSeekRequested={onSeekRequested}
        placeholderForNonFilledState={props.placeholderForNonFilledState}
      />
    </>
  );
};

type SwarovskiCarouselStaticPartProps = {
  bottomText: string | undefined;
  carouselElements: Array<React.ReactElement>;
  carouselWrapperStyles?: SerializedStyles;
  className: string;
  background?: "transparent" | "grey";
};

const RAW_SwarovskiCarouselStaticPart = React.forwardRef<HTMLDivElement, SwarovskiCarouselStaticPartProps>(
  (props, ref) => {
    return (
      <React.Fragment>
        <div
          ref={ref}
          className={cn(
            "js-carousel-wrapper",
            props.className,
            "u-hide-scrollbar-x",
            props.background === "grey" ? "variant-with-background" : undefined,
          )}
          css={[internalCarouselWrapperStyles, props.carouselWrapperStyles]}
        >
          {props.carouselElements.map((element, idx) => {
            return (
              <div key={idx} css={elementStyles}>
                {element}
              </div>
            );
          })}
        </div>

        {props.bottomText ? (
          <ImageCarouselComponentBottomText>{props.bottomText}</ImageCarouselComponentBottomText>
        ) : (
          <span
            css={css`
              display: none;
              ${CSS_MQ.atLeastMd} {
                height: 20px;
                display: block;
              }
            `}
          />
        )}
      </React.Fragment>
    );
  },
);

const SwarovskiCarouselStaticPart = React.memo(RAW_SwarovskiCarouselStaticPart, isEqual);

const ImageCarouselComponentBottomText = styled.span`
  ${FONTS.SwarovskiFutura300};
  font-size: 16px;
  color: ${CSS_COLORS.Grey3};
  letter-spacing: 0.8px;

  margin-top: 10px;
  ${CSS_MQ.atLeastMd} {
    margin-top: 15px;
  }
  display: block;
`;

const elementStyles = css`
  width: var(--w);
  max-width: 100%;

  flex-shrink: 0;

  .variant-with-background & {
    padding: 0;
    overflow: hidden;
  }

  padding-top: 5px;
  padding-bottom: 5px;

  .scroll-snap & {
    scroll-snap-align: start;
  }
`;

const internalCarouselWrapperStyles = css`
  overflow-x: scroll;

  display: flex;

  &.scroll-snap:not(.scrolling) {
    @supports (scroll-behavior: smooth) {
      scroll-behavior: smooth;
    }

    scroll-snap-type: x mandatory;
    scroll-snap-align: start;
  }

  scroll-behavior: smooth;
  overscroll-behavior-x: contain;

  &.variant-with-background {
    gap: 25px;

    margin-bottom: 20px;
  }
`;

const internalWrapperContainerStyles = css`
  width: fit-content;

  &.wrapper {
    max-width: 100%;
  }

  &.centered {
    margin-left: auto;
    margin-right: auto;
  }

  &:not(.centered) {
    margin-left: 0;
    margin-right: 0;
  }

  ${CSS_MQ.atMostSm} {
    margin-left: 20px;
  }
`;
